import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { motion } from 'framer-motion';
import {
  Users,
  Ban,
  Coins,
  Search,
  Trash2,
  Lock,
  Plus,
  UserMinus,
} from 'lucide-react';

export const UsersTab = ({
  isItalian,
  currentUserId,
  filteredUsers,
  searchQuery,
  setSearchQuery,
  selectedUserIds,
  toggleSelectAll,
  toggleUserSelection,
  allSelectableSelected,
  handleUpdateRole,
  handleToggleBan,
  handleOpenSendCoins,
  handleOpenModifyCoins,
  handleOpenChangePassword,
  handleDeleteUser,
  setShowBulkDeleteDialog,
  setShowCreateUserDialog,
  handleReleasePlayer,
  clubs = [],
  getRoleBadge,
  usersTotal = 0,
  usersPage = 0,
  usersPerPage = 20,
  onPageChange,
}) => {
  const navigate = useNavigate();
  const totalPages = Math.ceil(usersTotal / usersPerPage);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-[#0a0d15] border border-white/10"
    >
      <div className="p-4 border-b border-white/10">
        <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              placeholder={isItalian ? 'Cerca utenti...' : 'Search users...'}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-[#050508] border-white/10 text-white"
              data-testid="user-search-input"
            />
          </div>
          {selectedUserIds.size > 0 && (
            <Button
              className="bg-[#FF1744] hover:bg-[#FF1744]/80 text-white shrink-0"
              onClick={() => setShowBulkDeleteDialog(true)}
              data-testid="bulk-delete-users-btn"
            >
              <Trash2 size={16} className="mr-2" />
              {isItalian ? `Elimina (${selectedUserIds.size})` : `Delete (${selectedUserIds.size})`}
            </Button>
          )}
          <Button
            className="bg-[#00E676] hover:bg-[#00E676]/90 text-black font-bold shrink-0"
            onClick={() => setShowCreateUserDialog(true)}
            data-testid="create-user-btn"
          >
            <Plus size={16} className="mr-2" />
            {isItalian ? 'Crea Utente' : 'Create User'}
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="w-10">
                <input
                  type="checkbox"
                  checked={allSelectableSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-white/20 bg-transparent accent-[#FF1744] cursor-pointer"
                  data-testid="select-all-users-checkbox"
                />
              </TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Utente' : 'User'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Email</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">{isItalian ? 'Ruolo' : 'Role'}</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs">Status</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">Coins</TableHead>
              <TableHead className="text-slate-500 uppercase tracking-wider text-xs text-right">{isItalian ? 'Azioni' : 'Actions'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredUsers.map((u) => (
              <TableRow
                key={u.user_id}
                className={`border-white/10 hover:bg-white/5 ${selectedUserIds.has(u.user_id) ? 'bg-[#FF1744]/5' : ''}`}
              >
                <TableCell className="w-10">
                  {u.user_id !== currentUserId ? (
                    <input
                      type="checkbox"
                      checked={selectedUserIds.has(u.user_id)}
                      onChange={() => toggleUserSelection(u.user_id)}
                      className="w-4 h-4 rounded border-white/20 bg-transparent accent-[#FF1744] cursor-pointer"
                      data-testid={`select-user-${u.user_id}`}
                    />
                  ) : null}
                </TableCell>
                <TableCell
                  className="cursor-pointer"
                  onClick={() => navigate(`/profile/${u.user_id}`)}
                  data-testid={`user-profile-link-${u.user_id}`}
                >
                  <div>
                    <p className="font-medium text-white hover:text-[#00F0FF] transition-colors">{u.name}</p>
                    <div className="flex items-center gap-1.5">
                      {u.gamertag && (
                        <p className="text-sm text-[#00F0FF]">{u.gamertag}</p>
                      )}
                      {u.platform && (
                        <span
                          className={`px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider rounded border ${
                            u.platform === 'PS5'
                              ? 'bg-[#2979FF]/15 text-[#2979FF] border-[#2979FF]/30'
                              : u.platform === 'Xbox'
                              ? 'bg-[#00E676]/15 text-[#00E676] border-[#00E676]/30'
                              : 'bg-[#FF9100]/15 text-[#FF9100] border-[#FF9100]/30'
                          }`}
                          data-testid={`user-platform-${u.user_id}`}
                        >
                          {u.platform}
                        </span>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className="text-slate-400 text-sm cursor-pointer hover:text-[#00F0FF] transition-colors"
                  onClick={() => navigate(`/profile/${u.user_id}`)}
                >{u.email}</TableCell>
                <TableCell>{getRoleBadge(u.role)}</TableCell>
                <TableCell>
                  {u.is_banned ? (
                    <span className="px-2 py-1 text-xs rounded bg-[#FF1744]/20 text-[#FF1744] border border-[#FF1744]/30">
                      Banned
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs rounded bg-[#00E676]/20 text-[#00E676] border border-[#00E676]/30">
                      {isItalian ? 'Attivo' : 'Active'}
                    </span>
                  )}
                </TableCell>
                <TableCell className="text-right font-mono text-[#FFEA00]">
                  {(u.personal_coins || 0).toLocaleString()}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-2">
                    {u.user_id !== currentUserId && (
                      <Select
                        value={u.role}
                        onValueChange={(value) => handleUpdateRole(u.user_id, value)}
                      >
                        <SelectTrigger className="w-32 h-8 bg-transparent border-white/10 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-[#0a0d15] border-white/10">
                          <SelectItem value="player">{isItalian ? 'Giocatore' : 'Player'}</SelectItem>
                          <SelectItem value="captain">{isItalian ? 'Capitano' : 'Captain'}</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                          <SelectItem value="super_admin">Super Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                    <Button
                      size="icon"
                      variant="ghost"
                      className={`h-8 w-8 ${u.is_banned ? 'text-[#00E676]' : 'text-[#FF1744]'}`}
                      onClick={() => handleToggleBan(u.user_id)}
                    >
                      <Ban size={14} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-[#FFEA00] hover:text-[#FFEA00] hover:bg-[#FFEA00]/10"
                      onClick={() => handleOpenSendCoins(u)}
                      title={isItalian ? 'Invia Coins' : 'Send Coins'}
                    >
                      <Coins size={14} />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8 text-[#FF9100] hover:text-[#FF9100] hover:bg-[#FF9100]/10 font-bold"
                      onClick={() => handleOpenModifyCoins(u)}
                      title={isItalian ? 'Modifica Coins (aggiungi/rimuovi)' : 'Modify Coins (add/remove)'}
                      data-testid={`modify-coins-${u.user_id}`}
                    >
                      ±
                    </Button>
                    {u.user_id !== currentUserId && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 border-[#FF9100]/50 text-[#FF9100] hover:bg-[#FF9100]/20 hover:text-[#FF9100] font-bold text-xs gap-1"
                        onClick={() => handleOpenChangePassword(u)}
                        title={isItalian ? 'Cambia Password' : 'Change Password'}
                        data-testid={`change-password-${u.user_id}`}
                      >
                        <Lock size={12} />
                        PW
                      </Button>
                    )}
                    {u.club_id && !clubs.some(c => c.captain_id === u.user_id) && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-[#FF9100] hover:text-[#FF9100] hover:bg-[#FF9100]/10"
                        onClick={() => handleReleasePlayer(u)}
                        title={isItalian ? 'Svincola dal club' : 'Release from club'}
                        data-testid={`release-player-${u.user_id}`}
                      >
                        <UserMinus size={14} />
                      </Button>
                    )}
                    {u.user_id !== currentUserId && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8 text-[#FF1744] hover:text-[#FF1744] hover:bg-[#FF1744]/10"
                        onClick={() => handleDeleteUser(u)}
                        data-testid={`delete-user-${u.user_id}`}
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {filteredUsers.length === 0 && (
          <div className="p-8 text-center text-slate-500">
            <Users size={32} className="mx-auto mb-2 opacity-50" />
            <p>{isItalian ? 'Nessun utente trovato' : 'No users found'}</p>
          </div>
        )}
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-white/10" data-testid="users-pagination">
            <span className="text-sm text-slate-400">
              {isItalian 
                ? `${usersPage * usersPerPage + 1}-${Math.min((usersPage + 1) * usersPerPage, usersTotal)} di ${usersTotal} utenti`
                : `${usersPage * usersPerPage + 1}-${Math.min((usersPage + 1) * usersPerPage, usersTotal)} of ${usersTotal} users`
              }
            </span>
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={usersPage === 0}
                onClick={() => onPageChange(usersPage - 1)}
                className="border-white/10 text-white hover:bg-white/10"
                data-testid="users-prev-page"
              >
                {isItalian ? 'Prec.' : 'Prev'}
              </Button>
              <span className="flex items-center px-3 text-sm text-slate-300">
                {usersPage + 1} / {totalPages}
              </span>
              <Button
                size="sm"
                variant="outline"
                disabled={usersPage >= totalPages - 1}
                onClick={() => onPageChange(usersPage + 1)}
                className="border-white/10 text-white hover:bg-white/10"
                data-testid="users-next-page"
              >
                {isItalian ? 'Succ.' : 'Next'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
};
